import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import type { Party, WeeklyBatch, BatchAttachment } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import { Btn, Card } from '../ui/components';
import {
  Button,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function NewBatchPage() {
  const nav = useNavigate();
  const uplines = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });

  const [uplinePartyId, setUplinePartyId] = useState<string>('');
  const [weekMonday, setWeekMonday] = useState<string>(() => lastMondayIso());
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async (): Promise<{ batch: WeeklyBatch; created: boolean }> => {
      if (!uplinePartyId) throw new Error('Pick an upline.');
      if (!weekMonday) throw new Error('Pick a Monday date.');
      if (!isMonday(weekMonday)) throw new Error('Date must be a Monday.');
      const res = await api.post<{ batch: WeeklyBatch; created: boolean }>(
        '/api/batches',
        {
          uplinePartyId: Number(uplinePartyId),
          weekMonday,
        },
      );
      // Upload attachments (if any) after the batch exists.
      for (const f of files) {
        const base64 = await fileToBase64(f);
        await api.post<unknown>(`/api/batches/${res.batch.id}/attachments`, {
          base64,
          originalFilename: f.name,
          mimeType: f.type || 'application/octet-stream',
        });
      }
      return res;
    },
    onSuccess: ({ batch }) => {
      nav(`/batches/${batch.id}`);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <AppShell
      title="New batch"
      toolbar={
        <PageToolbar
          back={{ to: '/batches', label: 'All batches' }}
          title="New batch"
        />
      }
    >
      <div className="mx-auto max-w-2xl">
        <Card>
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              createMut.mutate();
            }}
          >
            <Select
              label="Upline"
              value={uplinePartyId}
              onChange={setUplinePartyId}
              options={(uplines.data ?? []).map((u) => ({
                value: String(u.id),
                label: u.displayName,
              }))}
              placeholder="Choose…"
            />
            <TextInput
              label="Week (Monday)"
              type="date"
              value={weekMonday}
              onChange={(e) => setWeekMonday(e.target.value)}
              hint="Must be a Monday."
              error={weekMonday && !isMonday(weekMonday) ? 'Not a Monday.' : null}
            />

            <div className="col-span-full">
              <label className="block text-small font-medium text-ink">
                Attach source files{' '}
                <span className="font-normal text-ink-muted">
                  (optional — upline sheet screenshots or .xlsx)
                </span>
              </label>
              <div
                className="mt-1.5 rounded-md border border-dashed border-border bg-surface-raised/50 p-4 text-center transition-colors hover:bg-surface-raised"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const dropped = Array.from(e.dataTransfer.files);
                  setFiles((prev) => [...prev, ...dropped]);
                }}
              >
                <input
                  type="file"
                  multiple
                  accept=".xlsx,.jpg,.jpeg,.png,.pdf"
                  className="block w-full text-small text-ink-muted file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-1 file:text-small file:text-ink transition-colors"
                  onChange={(e) => {
                    if (!e.target.files) return;
                    setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
                  }}
                />
                <p className="mt-2 text-caption text-ink-muted">
                  Drop files here or pick. Attachments are de-duped by SHA-256.
                </p>
              </div>

              {files.length > 0 && (
                <ul className="mt-2 space-y-1.5">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
                    >
                      <span className="flex items-center gap-2 text-small text-ink">
                        <Pill tone="neutral">{(f.size / 1024).toFixed(0)} kB</Pill>
                        <span className="truncate max-w-[20ch]">{f.name}</span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() =>
                          setFiles((prev) => prev.filter((_, idx) => idx !== i))
                        }
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && (
              <div className="col-span-full">
                <ErrorBanner>{error}</ErrorBanner>
              </div>
            )}

            <div className="col-span-full flex items-center gap-3 pt-1">
              <Btn type="submit" loading={createMut.isPending}>
                Create / open batch
              </Btn>
              <span className="text-caption text-ink-muted">
                If a batch already exists for this key, it&apos;ll open instead.
              </span>
            </div>
          </form>
        </Card>
      </div>
    </AppShell>
  );
}

function fileToBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('unexpected FileReader result'));
        return;
      }
      // strip the `data:*;base64,` prefix
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(f);
  });
}

function isMonday(isoDate: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return false;
  const d = new Date(`${isoDate}T00:00:00Z`);
  return d.getUTCDay() === 1;
}

function lastMondayIso(): string {
  const today = new Date();
  const day = today.getUTCDay(); // 0=Sun, 1=Mon
  const deltaDays = day === 0 ? -6 : 1 - day;
  const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + deltaDays));
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day2 = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day2}`;
}
// Unused import helper kept intentionally; referenced by the attachments upload shape.
void (null as unknown as BatchAttachment);
